// PERFECT 10 — the 4th POWERBALL tier, rendered inline as a tab in
// PowerballPage. Cross-tier conviction distillation: the 10 highest-conviction
// legs across PB10 / MEGA25 / Mixed Mega, scored deterministically.
//
// Surfaces (all inline — no new routes, per the 4th-tab-only decision):
//   • Slip summary bar (avg conviction, refresh time, Copy Slip)
//   • Conviction leg cards (rank, tier badge, conviction bar, odds)
//   • Empty / insufficient-fixtures state
//   • History accordion
//   • Admin strip (founder only): regenerate, scoring table, exclusions
//
// Reuses the LOCKED pb-prem-* design language; Perfect-10-specific chrome
// lives in perfect10.css.

import { useEffect, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import { useIsAdmin } from "../../sportCoverage/useIsAdmin.ts";
import {
  usePerfect10,
  type Perfect10Leg,
  type Perfect10Slip,
  type Perfect10Tier,
} from "../../perfect10/usePerfect10.ts";
import { usePerfect10Admin } from "../../perfect10/usePerfect10Admin.ts";
import { marketLabel, selectionLabel } from "../../exchange/market-format.ts";
import {
  FpAwait, fmtOdds, fmtKick, leagueCountry, countryFromCode,
} from "../football/_fpkit.tsx";
import "../../styles/perfect10.css";

// ── Tier metadata (doc appendix) ───────────────────────────
const TIER_META: Record<Perfect10Tier, { label: string; cls: string }> = {
  pb10: { label: "PB10", cls: "t-pb10" },
  mega25: { label: "MEGA25", cls: "t-mega25" },
  mixed_mega: { label: "MIXED", cls: "t-mixed" },
  lower_lines: { label: "LOWER LINES", cls: "t-mixed" },
  goals_bank: { label: "GOALS BANK", cls: "t-mixed" },
  big_stage: { label: "THE BIG STAGE", cls: "t-mixed" },
  multiverse: { label: "MULTIVERSE", cls: "t-mixed" },
};

function relTime(iso: string | null): string {
  if (!iso) return "—";
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "—";
  const mins = Math.round((Date.now() - t) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

// ── Match hero — picked side highlighted ───────────────────
function matchHero(leg: Perfect10Leg): JSX.Element {
  const match = String(leg.match_name ?? "").trim();
  const m = match.match(/^(.+?)\s+(?:vs?\.?|-|@)\s+(.+)$/i);
  if (!m) return <span>{match || "TBD"}</span>;
  const home = m[1]!.trim();
  const away = m[2]!.trim();
  const pick = (leg.pick ?? "").toLowerCase();
  const homePicked = pick === "home" || pick === home.toLowerCase();
  const awayPicked = pick === "away" || pick === away.toLowerCase();
  return (
    <span>
      <span className={homePicked ? "pickside" : undefined}>{home}</span>
      <span style={{ opacity: 0.5 }}> vs </span>
      <span className={awayPicked ? "pickside" : undefined}>{away}</span>
    </span>
  );
}

// ── Copy formatter (doc §9) ────────────────────────────────
function formatSlipForClipboard(slip: Perfect10Slip): string {
  const header = `⚡ POWERBALL PERFECT 10 — ${slip.generated_at ? new Date(slip.generated_at).toDateString() : "today"}\n`;
  const legs = slip.legs
    .map((leg, i) =>
      `${i + 1}. ${leg.match_name} | ${marketLabel(leg.market)} | ${selectionLabel(leg.market, leg.pick)} @ ${leg.odds.toFixed(2)}`)
    .join("\n");
  const footer = `\nvia strikza.com`;
  return header + legs + footer;
}

// ── Conviction leg card ────────────────────────────────────
function LegCard(
  { leg, index, isAdmin, onExclude }:
  { leg: Perfect10Leg; index: number; isAdmin: boolean; onExclude: (l: Perfect10Leg) => void },
): JSX.Element {
  const tier = TIER_META[leg.source_tier] ?? TIER_META.pb10;
  const countryObj = countryFromCode(leg.country_code) ?? leagueCountry(leg.league, leg.league_short);
  const country = countryObj ? `${countryObj.flag} ${countryObj.name}` : "🌍";
  const ctxParts = [leg.league, country, leg.kickoff_at ? fmtKick(leg.kickoff_at) : null].filter(Boolean);

  return (
    <article className="p10-leg" style={{ animationDelay: `${index * 40}ms` }}>
      <div className="p10-leg-top">
        <span className="p10-leg-rank">#{index + 1}</span>
        <span className={`p10-tier ${tier.cls}`}>{tier.label}</span>
        {isAdmin && (
          <button type="button" className="p10-excl-btn" title="Exclude from future Perfect 10"
            onClick={() => onExclude(leg)}>
            <Icon name="info" size={11} /> exclude
          </button>
        )}
      </div>

      <div className="p10-leg-match">{matchHero(leg)}</div>
      {ctxParts.length > 0 && <div className="p10-leg-context">{ctxParts.join(" · ")}</div>}

      <div className="p10-leg-pickline">
        {marketLabel(leg.market)} · <b>{selectionLabel(leg.market, leg.pick)}</b>
      </div>

      <div className="p10-conv"><span className="p10-leg-odds">{fmtOdds(leg.odds)}</span></div>
    </article>
  );
}

// ── Admin strip ────────────────────────────────────────────
function AdminStrip(
  { slip, onChanged }: { slip: Perfect10Slip | null; onChanged: () => void },
): JSX.Element {
  const admin = usePerfect10Admin();
  const [showScores, setShowScores] = useState(false);
  const [showExcl, setShowExcl] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);

  const { loadLogs, loadExclusions } = admin;
  useEffect(() => { if (showScores) void loadLogs(); }, [showScores, loadLogs]);
  useEffect(() => { if (showExcl) void loadExclusions(); }, [showExcl, loadExclusions]);

  const doRegen = async () => {
    setConfirmRegen(false);
    const r = await admin.regenerate();
    if (r.ok) onChanged();
  };

  const latestLog = admin.logs[0];

  return (
    <div className="p10-admin">
      <div className="p10-admin-row">
        <span className="p10-admin-tag">ADMIN</span>
        {!confirmRegen ? (
          <button type="button" className="p10-admin-btn primary" disabled={admin.busy}
            onClick={() => setConfirmRegen(true)}>
            {admin.busy ? "Working…" : "Regenerate"}
          </button>
        ) : (
          <span className="p10-admin-confirm">
            Replace current slip?
            <button type="button" className="p10-admin-btn primary" onClick={doRegen}>Yes</button>
            <button type="button" className="p10-admin-btn" onClick={() => setConfirmRegen(false)}>No</button>
          </span>
        )}
        <button type="button" className="p10-admin-btn" onClick={() => setShowScores((v) => !v)}>
          {showScores ? "Hide" : "Scoring table"}
        </button>
        <button type="button" className="p10-admin-btn" onClick={() => setShowExcl((v) => !v)}>
          {showExcl ? "Hide" : "Exclusions"}
        </button>
      </div>

      {admin.lastResult && (
        <div className="p10-admin-note">
          {admin.lastResult.ok
            ? `✓ ${admin.lastResult.status === "failed"
                ? `Generation reported ${admin.lastResult.reason ?? "no slip"}`
                : `Slip generated — avg conviction ${admin.lastResult.avg_conviction ?? "—"}, ${admin.lastResult.legs_selected ?? 0} legs`}`
            : `✗ ${admin.lastResult.error ?? "failed"}`}
        </div>
      )}

      {showScores && (
        <div className="p10-scoretbl">
          {latestLog ? (
            <>
              <div className="p10-scoretbl-meta">
                Last run · considered {latestLog.total_legs_considered ?? "—"} ·
                after dedup {latestLog.total_legs_after_dedup ?? "—"} ·
                selected {latestLog.legs_selected ?? "—"} ·
                {latestLog.duration_ms ?? "—"}ms
                {latestLog.error_message ? ` · ${latestLog.error_message}` : ""}
              </div>
              <table>
                <thead>
                  <tr><th>Match</th><th>Tier</th><th>Conv</th><th>Base</th><th>Form</th>
                    <th>H2H</th><th>Depth</th><th>Rec</th><th>Val</th></tr>
                </thead>
                <tbody>
                  {latestLog.raw_scores.map((s) => (
                    <tr key={s.source_leg_id}>
                      <td>{s.match_name}</td>
                      <td>{(TIER_META[s.source_tier as Perfect10Tier] ?? TIER_META.pb10).label}</td>
                      <td className="strong">{Math.round(s.conviction_score)}</td>
                      <td>{Math.round(s.base)}</td><td>{Math.round(s.form)}</td>
                      <td>{Math.round(s.h2h)}</td><td>{Math.round(s.depth)}</td>
                      <td>{Math.round(s.recency)}</td><td>{Math.round(s.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : <div className="p10-scoretbl-meta">No generation logs yet.</div>}
        </div>
      )}

      {showExcl && (
        <div className="p10-scoretbl">
          {admin.exclusions.length === 0 ? (
            <div className="p10-scoretbl-meta">No active exclusions.</div>
          ) : (
            <table>
              <thead><tr><th>Match</th><th>Tier</th><th>Reason</th><th /></tr></thead>
              <tbody>
                {admin.exclusions.map((x) => (
                  <tr key={x.id}>
                    <td>{x.match_name ?? x.source_leg_id.slice(0, 8)}</td>
                    <td>{x.source_tier ?? "—"}</td>
                    <td>{x.reason ?? "—"}</td>
                    <td>
                      <button type="button" className="p10-admin-btn"
                        onClick={() => void admin.removeExclusion(x.id)}>remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      <span aria-hidden style={{ display: "none" }}>{String(slip?.id ?? "")}</span>
    </div>
  );
}

// ── History accordion ──────────────────────────────────────
function HistorySection(
  { rows }: { rows: ReturnType<typeof usePerfect10>["history"] },
): JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (rows.length === 0) return null;
  return (
    <div className="p10-history">
      <button type="button" className="p10-history-toggle" onClick={() => setOpen((v) => !v)}>
        <Icon name="chev" size={12} /> Perfect 10 history ({rows.length})
      </button>
      {open && (
        <div className="p10-history-list">
          {rows.map((r) => (
            <div key={r.id} className={`p10-history-row ${r.status}`}>
              <span className="p10-history-when">{r.generated_at ? new Date(r.generated_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"}</span>
              <span className="p10-history-trigger">{r.triggered_by ?? "—"}</span>
              <span className="p10-history-status">
                {r.status === "failed" ? (r.failure_reason ?? "failed") : `${r.leg_count} legs`}
              </span>
              <span className="p10-history-conv">
                {r.avg_conviction !== null ? `avg ${Math.round(r.avg_conviction)}` : "—"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Panel ──────────────────────────────────────────────────
export function Perfect10Panel(): JSX.Element {
  const isAdmin = useIsAdmin();
  const { slip, history, loading, error, refetch } = usePerfect10();
  const [copied, setCopied] = useState(false);
  const admin = usePerfect10Admin();

  const copySlip = async () => {
    if (!slip) return;
    try {
      await navigator.clipboard.writeText(formatSlipForClipboard(slip));
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked */ }
  };

  const onExclude = async (leg: Perfect10Leg) => {
    await admin.excludeLeg({
      source_leg_id: leg.id ? leg.id : "",
      source_tier: leg.source_tier,
      fixture_id: leg.fixture_id,
      match_name: leg.match_name,
      reason: "Admin exclusion from Perfect 10 card",
    });
    refetch();
  };

  if (loading) {
    return (
      <section className="p10">
        <div className="p10-head">
          <div>
            <h2 className="p10-title">⚡ Perfect 10</h2>
            <p className="p10-tagline">10 legs. Maximum conviction.</p>
          </div>
        </div>
        <FpAwait kicker="PERFECT 10" title="Distilling conviction…" body="Scoring every active leg across all POWERBALL tiers." ghosts={6} />
      </section>
    );
  }

  if (error) {
    return (
      <section className="p10">
        <FpAwait kicker="ERROR" title="Perfect 10 unavailable" body={error} icon="info" ghosts={0} />
      </section>
    );
  }

  const legs = slip?.legs ?? [];
  const hasSlip = !!slip && legs.length > 0;

  return (
    <section className="p10">
      <div className="p10-head">
        <div>
          <h2 className="p10-title">⚡ Perfect 10</h2>
          <p className="p10-tagline">10 legs. Maximum conviction. Distilled across every POWERBALL tier.</p>
        </div>
        <button type="button" className="p10-refresh" onClick={refetch}>
          <Icon name="refresh" size={12} /> {slip?.generated_at ? `Updated ${relTime(slip.generated_at)}` : "Refresh"}
        </button>
      </div>

      {isAdmin && <AdminStrip slip={slip} onChanged={refetch} />}

      {hasSlip ? (
        <>
          <div className="p10-summary">
            <div className="p10-summary-stat">
              <span className="num">{legs.length}</span><span className="lbl">legs</span>
            </div>
            <div className="p10-summary-mix">
              {(Object.keys(TIER_META) as Perfect10Tier[]).map((t) => {
                const n = legs.filter((l) => l.source_tier === t).length;
                if (n === 0) return null;
                return <span key={t} className={`p10-tier ${TIER_META[t].cls}`}>{TIER_META[t].label} {n}</span>;
              })}
            </div>
            <button type="button" className="p10-copy" onClick={copySlip}>
              <Icon name={copied ? "check" : "reports"} size={13} /> {copied ? "Copied" : "Copy Slip"}
            </button>
          </div>

          <div className="p10-legs">
            {legs.map((leg, i) => (
              <LegCard key={leg.id} leg={leg} index={i} isAdmin={isAdmin} onExclude={onExclude} />
            ))}
          </div>
        </>
      ) : (
        <FpAwait
          kicker="PERFECT 10"
          title={slip?.status === "failed" ? "Not enough qualified legs this cycle" : "No Perfect 10 yet for this cycle"}
          body={slip?.status === "failed"
            ? "Perfect 10 needs at least 10 deduplicated legs across the POWERBALL tiers. It refreshes every few hours — each cycle is a fresh attempt."
            : "The distillation runs after the POWERBALL tiers assemble. Check back shortly."}
          icon="clock"
        />
      )}

      <HistorySection rows={history} />
    </section>
  );
}
